import computedValues from './computedValues'
import emailBlacklist from './emailBlacklist'
import { FileUpload } from '../api/models/types'

export type fileDataType = { id: string; name?: string; status: string; url?: string }

export function validateInputBox(
  component: {
    input_type?: string
    isRequired?: boolean
    validate_value?: string
    validation_formula?: string
    custom_validation_function?: string
    range_max?: number | string
    range_min?: number | string
    range_step?: number
    integer_only?: boolean
  },
  dataVal: boolean | string | number,
  isValid: boolean,
  userData: string | object
) {
  if (!component) return true
  if (component.input_type === 'confirm') {
    const isEqualValues = dataVal === true
    return component.isRequired ? isEqualValues : dataVal === undefined ? true : isEqualValues
  }

  if (!component.validate_value) return true

  switch (component.validation_formula) {
    case 'email_address': {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(String(dataVal).toLowerCase()) && !`${dataVal}`.endsWith('.con')
    }

    case 'business_email': {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      const domain = String(dataVal).split('@')[1]
      return (
        re.test(String(dataVal).toLowerCase()) &&
        !emailBlacklist.includes(domain) &&
        !`${dataVal}`.endsWith('.con')
      )
    }

    case 'full_name':
      return String(dataVal).trim().split(' ').length > 1

    case 'custom': {
      const validationString = component.custom_validation_function || `() => false`
      const fn = Function(`"use strict";return (${validationString})`)
      return fn()(dataVal, userData)
    }
    case 'phone_number_us': {
      /* From: https://stackoverflow.com/questions/4338267/validate-phone-number-with-javascript */
      try {
        const regex = /^(?:\+1\s*)?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,4}$/im
        return regex.test(`${dataVal}`)
      } catch (error) {
        return false
      }
    }

    case 'number_range': {
      if (typeof dataVal === 'boolean') return false
      const actualNumber = typeof dataVal === 'string' ? parseFloat(dataVal) : dataVal
      const rangeMax = component.range_max ? parseFloat(`${component.range_max || 0}`) : null
      const rangeMin = component.range_min ? parseFloat(`${component.range_min || 0}`) : null
      let integerOnly = false
      integerOnly = component.range_step
        ? Number.isInteger(parseFloat(`${component.range_step}`))
        : false
      component.integer_only === true ? (integerOnly = true) : null
      return validate(rangeMax, rangeMin, actualNumber, integerOnly)
    }

    case 'date_range': {
      if (typeof dataVal === 'boolean') return false
      const actualDate = new Date(dataVal)
      const rangeMax = component.range_max ? new Date(component.range_max) : null
      const rangeMin = component.range_min ? new Date(component.range_min) : null
      return validate(rangeMax, rangeMin, actualDate, null)
    }

    default:
      return true
  }
}

/*
  * Validates a FileUpload component based on user data.
  * @param component - The FileUpload component to validate.
  * @param userData - The data provided by the user, which can be a single file or an array of files.
  * @return An object indicating whether the validation passed and which validation failed, if any.
 */
export function validateFileUpload(
  component: FileUpload,
  userData: fileDataType | fileDataType[] | undefined
): {
  isValid: boolean
  failedValidation: 'required' | 'maxFilesLimit' | null
} {
  const hasFileContent = component.multiple ? (userData as fileDataType[])?.length >= 1 : !!userData

  if (component.isRequired && !hasFileContent) {
    return { isValid: false, failedValidation: 'required' }
  }

  const isUnderMaxFileLimit = component.files_max_limit
    ? (userData as fileDataType[])?.length <= component.files_max_limit
    : true

  if (!isUnderMaxFileLimit) {
    return { isValid: false, failedValidation: 'maxFilesLimit' }
  }

  return { isValid: true, failedValidation: null }
}

function validate(rangeMax, rangeMin, value, integerOnly) {
  if (rangeMax && value > rangeMax) {
    return false
  }
  if (rangeMin && value < rangeMin) {
    return false
  }
  if (integerOnly) {
    return Number.isInteger(value)
  }
  return true
}

export function componentPassesValidation(c, userData) {
  /* If not required + no validation, pass */
  if (!c.isRequired && !c.validate_value) return true
  const dataVal =
    userData[c.key || c.id] !== undefined
      ? userData[c.key || c.id]
      : computedValues(c.defaultVal, userData)
  /* If no value, passes if not required, else fails */
  if ([undefined, null, ''].includes(dataVal)) return !c.isRequired
  // if (c.isRequired && !dataVal) return false

  /* Evaluate based on type of component */
  switch (c.type) {
    case 'InputBox':
      return validateInputBox(c, dataVal, true, userData)

    case 'StripeCheckout': {
      if (!c.isRequired) return true
      return c.collect_details ? dataVal === 'card_added' : dataVal === 'paid'
    }

    case 'CustomAuth': {
      if (!c.isRequired) return true
      return dataVal === 'signed_in'
    }

    case 'FileUpload':
      const validation = validateFileUpload(c, userData[c.key])
      return validation.isValid

    default:
      return !Array.isArray(dataVal) || dataVal.length > 0
  }
}
